import { useRef } from 'react'
import { Eyebrow, Segments } from '@/components/ui'
import { call } from '@/content/site'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import styles from './Call.module.css'
import { CallStage } from './CallStage'
import { Marquee } from './Marquee'
import { useMaskedReveal } from './motion'
import { UnderTheHood } from './UnderTheHood'

/**
 * "02 — The call": it feels like a person, not software.
 *
 * 1. A pinned (~250vh) call on a frosted-glass card: captions fill word by word as scroll
 *    plays the illustrative conversation, the waveform and orb follow the speaker, and task
 *    chips resolve as Vokarta gets things done.
 * 2. A slow multilingual marquee of the kinds of things owners ask.
 * 3. "Under the hood": the voice stack as a signal-flow band.
 *
 * Reduced motion swaps in still versions (keyed, so no inline animation state survives).
 * See docs/DESIGN.md, "Section direction → Call".
 */
export function Call() {
  const rootRef = useRef<HTMLElement>(null)
  const reduced = useReducedMotion()
  useMaskedReveal(rootRef)
  const mode = reduced ? 'still' : 'live'

  return (
    <section
      ref={rootRef}
      id="call"
      aria-labelledby="call-title"
      className={styles.root}
      data-theme="light"
    >
      <div className={`container ${styles.intro}`}>
        <Eyebrow className={styles.eyebrow}>{call.eyebrow}</Eyebrow>
        <h2 id="call-title" className={styles.title} data-reveal>
          <Segments segments={call.title} />
        </h2>
        <p className={styles.lead} data-reveal>
          {call.lead}
        </p>
      </div>
      <CallStage key={`stage-${mode}`} reduced={reduced} />
      <Marquee key={`marquee-${mode}`} reduced={reduced} />
      <UnderTheHood key={`hood-${mode}`} reduced={reduced} />
    </section>
  )
}
